import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { SideDrawer } from '../ui/SideDrawer';
import { formatCurrency } from '../../lib/utils';

const MODELS = ['none', 'cpc', 'cpm', 'flat'] as const;

export function SourceCostDrawer({
  open,
  publisher,
  clicksInRange,
  revenueInRange,
  profitBeforeCost,
  onClose,
  onSaved,
}: {
  open: boolean;
  publisher: {
    id: number;
    displayName?: string | null;
    subId: string;
    costModel?: 'none' | 'cpc' | 'cpm' | 'flat';
    costValue?: number;
  } | null;
  clicksInRange: number;
  revenueInRange: number;
  profitBeforeCost: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  // Init from the publisher's current values so opening an existing source
  // shows its saved model/value instead of resetting to none/0.
  const [model, setModel] = useState<'none' | 'cpc' | 'cpm' | 'flat'>(publisher?.costModel ?? 'none');
  const [value, setValue] = useState(String(publisher?.costValue ?? 0));

  // BUGFIX: useState initial value only runs on first mount — reopening the
  // drawer for a DIFFERENT row kept the previous row's model/value. Reset
  // from the newly-selected publisher each time the drawer opens.
  useEffect(() => {
    if (!open) return;
    setModel(publisher?.costModel ?? 'none');
    setValue(String(publisher?.costValue ?? 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisher?.id, open]);

  const save = useMutation({
    mutationFn: () =>
      apiClient.patch(`/api/v1/publishers/${publisher!.id}`, {
        costModel: model,
        costValue: Number(value) || 0,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['publishers'] });
      qc.invalidateQueries({ queryKey: ['stats-by-publisher'] });
      qc.invalidateQueries({ queryKey: ['stats-overview'] });
      onSaved();
    },
  });

  const cost =
    model === 'cpc' ? clicksInRange * (Number(value) || 0)
    : model === 'cpm' ? (clicksInRange / 1000) * (Number(value) || 0)
    : model === 'flat' ? Number(value) || 0
    : 0;
  const profit = profitBeforeCost - cost;
  const roi = cost > 0 ? (profit / cost) * 100 : null;

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title="SOURCE COST"
      subtitle={publisher ? `${publisher.displayName ?? publisher.subId} · ${publisher.subId}` : ''}
      footer={
        <div className="p-4">
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="w-full btn-neon"
          >
            {save.isPending ? 'SAVING...' : 'SAVE COST'}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="space-y-2">
          <label className="text-[10px] font-display uppercase tracking-wider text-text-tertiary">COST MODEL</label>
          <div className="flex gap-2 flex-wrap">
            {MODELS.map((m) => (
              <button
                key={m}
                onClick={() => setModel(m)}
                className={`px-3 py-1.5 text-[10px] font-display uppercase tracking-wider rounded border transition-all ${
                  model === m
                    ? 'bg-neon-cyan/15 border-neon-cyan text-neon-cyan'
                    : 'bg-bg-tertiary border-border-subtle text-text-secondary hover:border-neon-cyan/40'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-[10px] font-display uppercase tracking-wider text-text-tertiary">COST VALUE ($)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="input-cyber"
            placeholder="0.00"
          />
          <p className="text-[10px] font-jetbrains-mono text-text-tertiary">
            {model === 'cpc' ? 'PER CLICK' : model === 'cpm' ? 'PER 1000 CLICKS' : model === 'flat' ? 'DAILY BUDGET' : 'NO COST'}
          </p>
        </div>

        <div className="cyber-panel p-4 space-y-1.5">
          <div className="flex justify-between text-[11px] font-jetbrains-mono">
            <span className="text-text-secondary">CLICKS IN RANGE</span>
            <span className="text-text-primary">{clicksInRange.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-[11px] font-jetbrains-mono">
            <span className="text-text-secondary">SPEND IN RANGE</span>
            <span className="text-neon-orange">{formatCurrency(cost)}</span>
          </div>
          <div className="flex justify-between text-[11px] font-jetbrains-mono">
            <span className="text-text-secondary">PROFIT AFTER COST</span>
            <span className={profit >= 0 ? 'text-neon-green' : 'text-neon-red'}>{formatCurrency(profit)}</span>
          </div>
          {roi != null && (
            <div className="flex justify-between text-[11px] font-jetbrains-mono">
              <span className="text-text-secondary">ROI</span>
              <span className={roi >= 0 ? 'text-neon-green' : 'text-neon-red'}>{roi.toFixed(1)}%</span>
            </div>
          )}
        </div>
      </div>
    </SideDrawer>
  );
}
